import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Save, Users, Wallet, Pencil, FileText, Upload, Eye, Loader2, Download } from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import { AccountingLayout } from '../components/AccountingLayout';
import { AccountCombobox } from '../components/AccountCombobox';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  listEmployees, createEmployee, updateEmployee, deleteEmployee,
  listPayroll, createPayroll, deletePayroll,
  listAccounts, uploadFile,
} from '../lib/api';
import { formatRp, parseNumber, formatThousands, MONTHS_ID } from '../lib/format';

const todayIso = () => new Date().toISOString().slice(0, 10);
const nowD = new Date();
const currentPeriod = `${MONTHS_ID[nowD.getMonth()]} ${nowD.getFullYear()}`;

const monthsBetween = (fromIso) => {
  if (!fromIso) return '—';
  const from = new Date(fromIso);
  if (isNaN(from)) return '—';
  const now = new Date();
  const years = now.getFullYear() - from.getFullYear();
  const months = now.getMonth() - from.getMonth();
  let total = years * 12 + months;
  if (now.getDate() < from.getDate()) total -= 1;
  if (total < 0) return '—';
  const y = Math.floor(total / 12);
  const m = total % 12;
  if (y === 0) return `${m} bln`;
  if (m === 0) return `${y} thn`;
  return `${y} thn ${m} bln`;
};

const emptyEmp = () => ({
  name: '', position: '', join_date: '', base_salary: '',
  bank_name: '', bank_account: '',
  ktp_number: '', ktp_file: '', ktp_original_name: '',
});

export default function PayrollPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('runs');
  const [employees, setEmployees] = useState([]);
  const [runs, setRuns] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [openEmp, setOpenEmp] = useState(false);
  const [openRun, setOpenRun] = useState(false);
  const [openExport, setOpenExport] = useState(false);
  const [exportRange, setExportRange] = useState({ from: '', to: '' });
  const [editingEmpId, setEditingEmpId] = useState(null);
  const [empForm, setEmpForm] = useState(emptyEmp());
  const [ktpUploading, setKtpUploading] = useState(false);
  const handleKtpUpload = async (file) => {
    if (!file) return;
    const allowedExt = ['.jpg', '.jpeg', '.png', '.pdf'];
    const ext = ('.' + file.name.split('.').pop()).toLowerCase();
    if (!allowedExt.includes(ext)) {
      toast.error('Format harus JPG, JPEG, PNG, atau PDF');
      return;
    }
    setKtpUploading(true);
    try {
      const res = await uploadFile(file);
      setEmpForm((s) => ({ ...s, ktp_file: res.filename, ktp_original_name: res.original_name || file.name }));
      toast.success('KTP diupload');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal upload KTP');
    } finally { setKtpUploading(false); }
  };
  const handleKtpRemove = () => {
    setEmpForm((s) => ({ ...s, ktp_file: '', ktp_original_name: '' }));
    toast.success('Dokumen KTP dihapus');
  };
  const openKtpView = () => {
    if (!empForm.ktp_file) return;
    const backend = process.env.REACT_APP_BACKEND_URL || '';
    window.open(`${backend}/api/attachments/${empForm.ktp_file}`, '_blank', 'noopener,noreferrer');
  };


  const exportRunsToExcel = () => {
    const { from, to } = exportRange;
    if (!from || !to) { toast.error('Isi tanggal mulai & akhir'); return; }
    if (from > to) { toast.error('Tanggal mulai harus sebelum tanggal akhir'); return; }
    const rows = runs.filter((r) => r.date >= from && r.date <= to);
    if (rows.length === 0) {
      toast.error('Tidak ada data riwayat gaji pada periode yang dipilih.');
      return;
    }
    const rpFmt = '"Rp"#,##0';
    const headerStyle = {
      font: { bold: true, color: { rgb: 'FFFFFFFF' } },
      fill: { patternType: 'solid', fgColor: { rgb: 'FF065F46' } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border: {
        top: { style: 'thin', color: { rgb: 'FFCCCCCC' } },
        bottom: { style: 'thin', color: { rgb: 'FFCCCCCC' } },
        left: { style: 'thin', color: { rgb: 'FFCCCCCC' } },
        right: { style: 'thin', color: { rgb: 'FFCCCCCC' } },
      },
    };
    const titleStyle = {
      font: { bold: true, size: 14, color: { rgb: 'FFFFFFFF' } },
      fill: { patternType: 'solid', fgColor: { rgb: 'FF065F46' } },
      alignment: { horizontal: 'center', vertical: 'center' },
    };
    const totalStyle = {
      font: { bold: true },
      fill: { patternType: 'solid', fgColor: { rgb: 'FFECFDF5' } },
      border: { top: { style: 'medium', color: { rgb: 'FF065F46' } } },
    };
    const fmtDate = (iso) => {
      if (!iso) return '';
      const [y, m, d] = iso.split('-');
      return `${d}/${m}/${y}`;
    };
    const cols = 8;
    const sorted = [...rows].sort((a, b) => a.date.localeCompare(b.date));
    const aoa = [];
    aoa.push([{ v: 'RIWAYAT GAJI', s: titleStyle }, ...Array(cols - 1).fill('')]);
    aoa.push([{ v: `Periode: ${fmtDate(from)} – ${fmtDate(to)}` }]);
    aoa.push([]);
    aoa.push([
      { v: 'Tanggal', s: headerStyle },
      { v: 'Nama Karyawan', s: headerStyle },
      { v: 'Periode', s: headerStyle },
      { v: 'Gaji Pokok', s: headerStyle },
      { v: 'Komisi', s: headerStyle },
      { v: 'Potongan', s: headerStyle },
      { v: 'Total', s: headerStyle },
      { v: 'Ref', s: headerStyle },
    ]);
    let sumGP = 0, sumK = 0, sumP = 0, sumT = 0;
    for (const r of sorted) {
      const gp = Number(r.gaji_pokok || 0);
      const km = Number(r.komisi || 0);
      const pt = Number(r.potongan || 0);
      const tt = Number(r.amount || 0);
      sumGP += gp; sumK += km; sumP += pt; sumT += tt;
      aoa.push([
        fmtDate(r.date),
        r.employee_name || '',
        r.period || '',
        { v: gp, t: 'n', z: rpFmt },
        { v: km, t: 'n', z: rpFmt },
        { v: pt, t: 'n', z: rpFmt },
        { v: tt, t: 'n', z: rpFmt },
        r.journal_ref || '',
      ]);
    }
    aoa.push([
      { v: 'TOTAL', s: totalStyle }, '', '',
      { v: sumGP, t: 'n', z: rpFmt, s: totalStyle },
      { v: sumK, t: 'n', z: rpFmt, s: totalStyle },
      { v: sumP, t: 'n', z: rpFmt, s: totalStyle },
      { v: sumT, t: 'n', z: rpFmt, s: { ...totalStyle, font: { bold: true, color: { rgb: 'FF065F46' } } } },
      '',
    ]);
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [
      { wch: 12 }, { wch: 24 }, { wch: 16 }, { wch: 15 },
      { wch: 15 }, { wch: 15 }, { wch: 17 }, { wch: 12 },
    ];
    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: cols - 1 } }];
    ws['!rows'] = [{ hpt: 26 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Riwayat Gaji');
    XLSX.writeFile(wb, `Riwayat_Gaji_${from}_${to}.xlsx`);
    toast.success(`Excel diunduh (${rows.length} baris)`);
    setOpenExport(false);
  };

  const exportEmployeesToExcel = () => {
    if (employees.length === 0) {
      toast.error('Belum ada data karyawan');
      return;
    }
    if (employees.length === 0) {
      toast.error('Belum ada data karyawan');
      return;
    }
    const headerStyle = {
      font: { bold: true, color: { rgb: 'FFFFFFFF' } },
      fill: { patternType: 'solid', fgColor: { rgb: 'FF065F46' } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border: {
        top: { style: 'thin', color: { rgb: 'FFCCCCCC' } },
        bottom: { style: 'thin', color: { rgb: 'FFCCCCCC' } },
        left: { style: 'thin', color: { rgb: 'FFCCCCCC' } },
        right: { style: 'thin', color: { rgb: 'FFCCCCCC' } },
      },
    };
    const titleStyle = {
      font: { bold: true, size: 14, color: { rgb: 'FFFFFFFF' } },
      fill: { patternType: 'solid', fgColor: { rgb: 'FF065F46' } },
      alignment: { horizontal: 'center', vertical: 'center' },
    };
    const rpFmt = '"Rp"#,##0';
    const cols = 10;
    const aoa = [];
    aoa.push([{ v: 'DATA KARYAWAN', s: titleStyle }, ...Array(cols - 1).fill('')]);
    aoa.push([{ v: `Diekspor: ${new Date().toLocaleString('id-ID')}` }]);
    aoa.push([]);
    aoa.push([
      { v: 'No', s: headerStyle },
      { v: 'Nama', s: headerStyle },
      { v: 'Jabatan', s: headerStyle },
      { v: 'Tanggal Masuk', s: headerStyle },
      { v: 'Masa Kerja', s: headerStyle },
      { v: 'Gaji Pokok', s: headerStyle },
      { v: 'Bank', s: headerStyle },
      { v: 'No. Rekening', s: headerStyle },
      { v: 'No. KTP / NIK', s: headerStyle },
      { v: 'Dokumen KTP', s: headerStyle },
    ]);
    employees.forEach((e, i) => {
      aoa.push([
        i + 1,
        e.name || '',
        e.position || '',
        e.join_date || '',
        monthsBetween(e.join_date),
        { v: e.base_salary || 0, t: 'n', z: rpFmt },
        e.bank_name || '',
        { v: e.bank_account || '', t: 's' },
        { v: e.ktp_number || '', t: 's' },
        e.ktp_original_name || (e.ktp_file ? '(tersedia)' : '—'),
      ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [
      { wch: 5 }, { wch: 24 }, { wch: 18 }, { wch: 14 }, { wch: 14 },
      { wch: 16 }, { wch: 14 }, { wch: 18 }, { wch: 20 }, { wch: 22 },
    ];
    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: cols - 1 } }];
    ws['!rows'] = [{ hpt: 26 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Karyawan');
    const ts = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `Data_Karyawan_${ts}.xlsx`);
    toast.success(`Excel diunduh (${employees.length} karyawan)`);
  };
  // Proses Gaji multi-employee form
  const [runHeader, setRunHeader] = useState({
    date: todayIso(), period: currentPeriod,
    expense_account_id: '', cash_account_id: '',
  });
  // Map<employee_id, {selected, gaji_pokok, dasar_komisi, rate_komisi, potongan}>
  const [runRows, setRunRows] = useState({});

  const refresh = async () => {
    const [e, r, a] = await Promise.all([listEmployees(), listPayroll(), listAccounts({ is_active: true })]);
    setEmployees(e); setRuns(r); setAccounts(a);
    setRunHeader((h) => {
      const gaji = a.find((x) => x.code === '6-3001')
        || a.find((x) => x.code === '5-1000')
        || a.find((x) => x.type === 'expense' && !x.is_header && /gaji/i.test(x.name || ''))
        || a.find((x) => x.type === 'expense' && !x.is_header);
      const kas = a.find((x) => x.code === '1-1101')
        || a.find((x) => x.code === '1-1000')
        || a.find((x) => x.type === 'asset' && !x.is_header);
      return {
        ...h,
        expense_account_id: h.expense_account_id || gaji?.id || '',
        cash_account_id: h.cash_account_id || kas?.id || '',
      };
    });
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const expenseAccounts = useMemo(() => accounts.filter((a) => a.type === 'expense'), [accounts]);
  const cashAccounts = useMemo(() => accounts.filter((a) => a.type === 'asset' || a.type === 'liability'), [accounts]);

  // ---------- Employee CRUD ----------
  const openEmpCreate = () => {
    setEmpForm(emptyEmp());
    setEditingEmpId(null);
    setOpenEmp(true);
  };
  const openEmpEdit = (e) => {
    setEmpForm({
      name: e.name || '', position: e.position || '',
      join_date: e.join_date || '',
      base_salary: e.base_salary ? new Intl.NumberFormat('id-ID').format(e.base_salary) : '',
      bank_name: e.bank_name || '', bank_account: e.bank_account || '',
      ktp_number: e.ktp_number || '',
      ktp_file: e.ktp_file || '',
      ktp_original_name: e.ktp_original_name || '',
    });
    setEditingEmpId(e.id);
    setOpenEmp(true);
  };
  const saveEmp = async () => {
    if (!empForm.name.trim()) return toast.error('Nama wajib');
    const payload = { ...empForm, base_salary: parseNumber(empForm.base_salary) };
    try {
      if (editingEmpId) {
        await updateEmployee(editingEmpId, payload);
        toast.success('Karyawan diperbarui');
      } else {
        await createEmployee(payload);
        toast.success('Karyawan ditambahkan');
      }
      setOpenEmp(false);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal simpan'); }
  };
  const removeEmp = async (e) => {
    if (!window.confirm(`Hapus karyawan ${e.name}?`)) return;
    try { await deleteEmployee(e.id); toast.success('Karyawan dihapus'); refresh(); }
    catch (er) { toast.error(er?.response?.data?.detail || 'Gagal hapus'); }
  };

  // ---------- Proses Gaji ----------
  const openProsesGaji = () => {
    // Preselect all active employees with their base salary
    const initial = {};
    employees.forEach((e) => {
      initial[e.id] = {
        selected: true,
        gaji_pokok: e.base_salary ? new Intl.NumberFormat('id-ID').format(e.base_salary) : '',
        dasar_komisi: '', rate_komisi: '', potongan: '',
      };
    });
    setRunRows(initial);
    setOpenRun(true);
  };
  const toggleRow = (id, key, value) => {
    setRunRows((r) => ({ ...r, [id]: { ...(r[id] || {}), [key]: value } }));
  };
  const rowTotal = (r) => {
    if (!r) return 0;
    const gp = parseNumber(r.gaji_pokok);
    const dk = parseNumber(r.dasar_komisi);
    const rate = parseFloat(String(r.rate_komisi).replace(',', '.')) || 0;
    const komisi = dk * (rate / 100);
    const pot = parseNumber(r.potongan);
    return Math.max(0, gp + komisi - pot);
  };
  const rowKomisi = (r) => {
    const dk = parseNumber(r?.dasar_komisi || 0);
    const rate = parseFloat(String(r?.rate_komisi || 0).replace(',', '.')) || 0;
    return dk * (rate / 100);
  };
  const totalGaji = useMemo(() => {
    return employees.reduce((sum, e) => {
      const row = runRows[e.id];
      if (!row?.selected) return sum;
      return sum + rowTotal(row);
    }, 0);
  }, [employees, runRows]);

  const submitProses = async () => {
    if (!runHeader.expense_account_id || !runHeader.cash_account_id) return toast.error('Pilih akun beban & kas/bank');
    const selected = employees.filter((e) => runRows[e.id]?.selected);
    if (selected.length === 0) return toast.error('Pilih minimal 1 karyawan');
    let ok = 0, fail = 0;
    for (const e of selected) {
      const r = runRows[e.id];
      const amount = rowTotal(r);
      if (amount <= 0) { fail++; continue; }
      const komisi = rowKomisi(r);
      try {
        await createPayroll({
          employee_id: e.id,
          date: runHeader.date,
          period: runHeader.period,
          description: '',
          gaji_pokok: parseNumber(r.gaji_pokok),
          dasar_komisi: parseNumber(r.dasar_komisi),
          rate_komisi: parseFloat(String(r.rate_komisi).replace(',', '.')) || 0,
          komisi,
          potongan: parseNumber(r.potongan),
          amount,
          expense_account_id: runHeader.expense_account_id,
          cash_account_id: runHeader.cash_account_id,
        });
        ok++;
      } catch (err) {
        fail++;
      }
    }
    toast[fail ? 'warning' : 'success'](`Berhasil: ${ok}${fail ? ` · Gagal: ${fail}` : ''} — Jurnal dibuat.`);
    setOpenRun(false);
    refresh();
  };

  const removeRun = async (r) => {
    if (!window.confirm(`Hapus riwayat gaji ${r.employee_name}? Jurnal terkait juga terhapus.`)) return;
    try { await deletePayroll(r.id); toast.success('Riwayat dihapus'); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Gagal hapus'); }
  };

  return (
    <AccountingLayout>
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-emerald-800 font-semibold mb-2">
            JurnalKu · Penggajian
          </div>
          <h1 className="font-heading text-3xl font-semibold text-stone-900 tracking-tight flex items-center gap-2">
            <Users className="w-6 h-6 text-emerald-800" /> Payroll
          </h1>
          <p className="text-sm text-stone-600 mt-2">
            Proses gaji multi-karyawan otomatis membuat jurnal <span className="font-mono text-xs">D: Beban Gaji</span> · <span className="font-mono text-xs">K: Kas/Bank</span>.
          </p>
        </div>
        <div className="flex gap-2">
          {tab === 'employees' ? (
            <>
              <Button onClick={exportEmployeesToExcel} variant="outline" data-testid="export-employees-btn">
                <Download className="w-4 h-4 mr-1" /> Export Excel
              </Button>
              <Button onClick={openEmpCreate} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="add-employee-btn">
                <Plus className="w-4 h-4 mr-1" /> Karyawan Baru
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => setOpenExport(true)} variant="outline" data-testid="export-runs-btn">
                <Download className="w-4 h-4 mr-1" /> Export Excel
              </Button>
              <Button onClick={openProsesGaji} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="add-payroll-btn">
                <Plus className="w-4 h-4 mr-1" /> Proses Gaji
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <Button variant={tab === 'runs' ? 'default' : 'outline'} onClick={() => setTab('runs')}
          className={tab === 'runs' ? 'bg-emerald-800 hover:bg-emerald-900 text-white' : ''}
          data-testid="tab-payroll-runs"
        >
          <Wallet className="w-4 h-4 mr-1.5" /> Riwayat Gaji
        </Button>
        <Button variant={tab === 'employees' ? 'default' : 'outline'} onClick={() => setTab('employees')}
          className={tab === 'employees' ? 'bg-emerald-800 hover:bg-emerald-900 text-white' : ''}
          data-testid="tab-employees"
        >
          <Users className="w-4 h-4 mr-1.5" /> Karyawan
        </Button>
      </div>

      {tab === 'runs' && (
        <div className="card-modern overflow-hidden">
          <table className="w-full ledger">
            <thead>
              <tr>
                <th style={{ width: '12%' }}>Tanggal</th>
                <th>Karyawan</th>
                <th>Periode</th>
                <th style={{ width: '12%' }} className="text-right">Gaji Pokok</th>
                <th style={{ width: '10%' }} className="text-right">Komisi</th>
                <th style={{ width: '10%' }} className="text-right">Potongan</th>
                <th style={{ width: '12%' }} className="text-right">Total</th>
                <th style={{ width: '8%' }}>Ref</th>
                <th style={{ width: '6%' }}></th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id}>
                  <td className="num text-sm">{r.date}</td>
                  <td className="font-medium text-stone-800">{r.employee_name}</td>
                  <td className="text-sm text-stone-600">{r.period || '—'}</td>
                  <td className="num-cell text-xs">{formatRp(r.gaji_pokok || 0)}</td>
                  <td className="num-cell text-xs text-emerald-800">{r.komisi ? formatRp(r.komisi) : '—'}</td>
                  <td className="num-cell text-xs text-red-700">{r.potongan ? formatRp(r.potongan) : '—'}</td>
                  <td className="num-cell font-semibold">{formatRp(r.amount)}</td>
                  <td className="font-mono text-xs text-emerald-900">{r.journal_ref}</td>
                  <td>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" onClick={() => navigate(`/payroll/slip/${r.id}`)}
                        className="border-emerald-200 text-emerald-800 hover:bg-emerald-50"
                        data-testid={`slip-${r.id}`} title="Slip gaji">
                        <FileText className="w-3 h-3" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => removeRun(r)}
                        className="border-red-200 text-red-700 hover:bg-red-50">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {runs.length === 0 && (
                <tr><td colSpan={9} className="text-center text-stone-500 py-8">Belum ada pembayaran gaji.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'employees' && (
        <div className="card-modern overflow-hidden">
          <table className="w-full ledger">
            <thead>
              <tr>
                <th>Nama</th>
                <th style={{ width: '20%' }}>Jabatan</th>
                <th style={{ width: '15%' }}>Masa Kerja</th>
                <th style={{ width: '22%' }}>Bank / No. Rekening</th>
                <th style={{ width: '15%' }} className="text-right">Gaji Pokok</th>
                <th style={{ width: '12%' }}></th>
              </tr>
            </thead>
            <tbody>
              {employees.map((e) => (
                <tr key={e.id}>
                  <td className="font-medium text-stone-800">{e.name}</td>
                  <td className="text-sm text-stone-600">{e.position || '—'}</td>
                  <td className="text-sm text-stone-600">{monthsBetween(e.join_date)}</td>
                  <td className="text-sm text-stone-600">
                    {e.bank_name || e.bank_account ? (
                      <>
                        <div className="font-medium text-stone-700">{e.bank_name || '—'}</div>
                        <div className="font-mono text-xs text-stone-500">{e.bank_account || ''}</div>
                      </>
                    ) : '—'}
                  </td>
                  <td className="num-cell">{formatRp(e.base_salary)}</td>
                  <td>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" onClick={() => openEmpEdit(e)}
                        className="border-emerald-200 text-emerald-800 hover:bg-emerald-50"
                        data-testid={`edit-employee-${e.id}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => removeEmp(e)}
                        className="border-red-200 text-red-700 hover:bg-red-50">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {employees.length === 0 && (
                <tr><td colSpan={6} className="text-center text-stone-500 py-8">Belum ada karyawan.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Employee dialog */}
      <Dialog open={openEmp} onOpenChange={setOpenEmp}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingEmpId ? 'Edit Karyawan' : 'Karyawan Baru'}</DialogTitle>
            <DialogDescription>Data karyawan untuk pemrosesan gaji.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Nama *</Label>
              <Input value={empForm.name} onChange={(e) => setEmpForm({ ...empForm, name: e.target.value })}
                data-testid="employee-form-name" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Jabatan</Label>
              <Input value={empForm.position} onChange={(e) => setEmpForm({ ...empForm, position: e.target.value })} />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Tanggal Masuk</Label>
              <Input type="date" value={empForm.join_date}
                onChange={(e) => setEmpForm({ ...empForm, join_date: e.target.value })}
                data-testid="employee-form-join-date" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Gaji Pokok (Rp)</Label>
              <Input value={empForm.base_salary}
                onChange={(e) => setEmpForm({ ...empForm, base_salary: formatThousands(e.target.value) })}
                className="text-right font-mono" placeholder="0" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Nama Bank</Label>
              <Input value={empForm.bank_name} onChange={(e) => setEmpForm({ ...empForm, bank_name: e.target.value })}
                placeholder="BCA / Mandiri / BRI" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">No. Rekening</Label>
              <Input value={empForm.bank_account} onChange={(e) => setEmpForm({ ...empForm, bank_account: e.target.value })}
                className="font-mono" placeholder="1234567890" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">No. KTP / NIK</Label>
              <Input
                value={empForm.ktp_number}
                onChange={(e) => setEmpForm({ ...empForm, ktp_number: e.target.value.replace(/[^\d]/g, '').slice(0, 16) })}
                inputMode="numeric"
                maxLength={16}
                className="font-mono"
                placeholder="16 digit"
                data-testid="employee-form-ktp-number"
              />
              {empForm.ktp_number && empForm.ktp_number.length !== 16 && (
                <div className="text-[10px] text-amber-700 mt-1">
                  {empForm.ktp_number.length}/16 digit
                </div>
              )}
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Dokumen KTP</Label>
              {!empForm.ktp_file ? (
                <>
                  <input
                    id="ktp-upload-input"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                    className="hidden"
                    onChange={(e) => { handleKtpUpload(e.target.files?.[0]); e.target.value = ''; }}
                    data-testid="employee-form-ktp-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={ktpUploading}
                    onClick={() => document.getElementById('ktp-upload-input')?.click()}
                    className="w-full justify-center"
                    data-testid="employee-form-ktp-upload"
                  >
                    {ktpUploading
                      ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Mengupload…</>
                      : <><Upload className="w-4 h-4 mr-1.5" /> Upload KTP</>}
                  </Button>
                  <div className="text-[10px] text-stone-500 mt-1">JPG, JPEG, PNG, atau PDF</div>
                </>
              ) : (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2">
                    <FileText className="w-4 h-4 text-emerald-800 shrink-0" />
                    <div className="text-xs text-emerald-900 truncate flex-1" title={empForm.ktp_original_name}>
                      {empForm.ktp_original_name || empForm.ktp_file}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <input
                      id="ktp-replace-input"
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                      className="hidden"
                      onChange={(e) => { handleKtpUpload(e.target.files?.[0]); e.target.value = ''; }}
                    />
                    <Button
                      type="button" size="sm" variant="outline"
                      onClick={openKtpView}
                      className="flex-1 h-8 text-xs"
                      data-testid="employee-form-ktp-view"
                    >
                      <Eye className="w-3 h-3 mr-1" /> Lihat
                    </Button>
                    <Button
                      type="button" size="sm" variant="outline"
                      disabled={ktpUploading}
                      onClick={() => document.getElementById('ktp-replace-input')?.click()}
                      className="flex-1 h-8 text-xs"
                      data-testid="employee-form-ktp-replace"
                    >
                      {ktpUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Upload className="w-3 h-3 mr-1" /> Ganti</>}
                    </Button>
                    <Button
                      type="button" size="sm" variant="outline"
                      onClick={handleKtpRemove}
                      className="h-8 text-xs text-red-700 border-red-200 hover:bg-red-50"
                      data-testid="employee-form-ktp-remove"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenEmp(false)}>Batal</Button>
            <Button onClick={saveEmp} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="employee-form-save">
              <Save className="w-4 h-4 mr-1" /> Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Proses Gaji dialog */}
      <Dialog open={openRun} onOpenChange={setOpenRun}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Proses Gaji</DialogTitle>
            <DialogDescription>Pilih karyawan, atur gaji pokok, komisi, dan potongan. Jurnal dibuat otomatis.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Periode</Label>
                <Input value={runHeader.period}
                  onChange={(e) => setRunHeader({ ...runHeader, period: e.target.value })}
                  placeholder="mis. Agustus 2026" data-testid="run-form-period" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Tanggal Bayar</Label>
                <Input type="date" value={runHeader.date}
                  onChange={(e) => setRunHeader({ ...runHeader, date: e.target.value })}
                  data-testid="run-form-date" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Dibayar dari (Kas/Bank)</Label>
                <AccountCombobox
                  value={runHeader.cash_account_id}
                  onChange={(v) => setRunHeader({ ...runHeader, cash_account_id: v })}
                  accounts={cashAccounts}
                  testId="run-form-cash"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Akun Beban Gaji</Label>
                <AccountCombobox
                  value={runHeader.expense_account_id}
                  onChange={(v) => setRunHeader({ ...runHeader, expense_account_id: v })}
                  accounts={expenseAccounts}
                  testId="run-form-expense"
                />
              </div>
            </div>

            <div className="border-t border-stone-200 pt-3">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="text-sm font-semibold text-stone-800">
                  Karyawan — atur gaji pokok, komisi &amp; bonus
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-stone-500 mr-1" data-testid="selected-count">
                    {employees.filter((e) => runRows[e.id]?.selected).length} / {employees.length} dipilih
                  </span>
                  <Button
                    type="button" size="sm" variant="outline"
                    className="h-7 px-2 text-xs"
                    onClick={() => {
                      setRunRows((r) => {
                        const next = { ...r };
                        employees.forEach((e) => { next[e.id] = { ...(next[e.id] || {}), selected: true }; });
                        return next;
                      });
                    }}
                    data-testid="btn-select-all"
                  >
                    Pilih Semua
                  </Button>
                  <Button
                    type="button" size="sm" variant="outline"
                    className="h-7 px-2 text-xs"
                    onClick={() => {
                      setRunRows((r) => {
                        const next = { ...r };
                        employees.forEach((e) => { next[e.id] = { ...(next[e.id] || {}), selected: false }; });
                        return next;
                      });
                    }}
                    data-testid="btn-unselect-all"
                  >
                    Batal Semua
                  </Button>
                </div>
              </div>
              <div className="space-y-3">
                {employees.map((e) => {
                  const row = runRows[e.id] || { selected: false };
                  const total = rowTotal(row);
                  const komisi = rowKomisi(row);
                  return (
                    <div key={e.id}
                      className={`border rounded-lg p-3 transition-colors ${row.selected ? 'bg-emerald-50/40 border-emerald-200' : 'border-stone-200 bg-white'}`}
                      data-testid={`proses-emp-${e.id}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <Checkbox checked={!!row.selected}
                            onCheckedChange={(v) => toggleRow(e.id, 'selected', !!v)} />
                          <div>
                            <span className="font-semibold text-stone-900">{e.name}</span>
                            {e.position && <span className="text-xs text-stone-500 ml-2">· {e.position}</span>}
                          </div>
                        </label>
                        <div className="num font-bold text-emerald-900">{formatRp(total)}</div>
                      </div>
                      {row.selected && (
                        <div className="grid grid-cols-5 gap-2">
                          <div>
                            <Label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 block">Gaji Pokok</Label>
                            <Input value={row.gaji_pokok || ''}
                              onChange={(ev) => toggleRow(e.id, 'gaji_pokok', formatThousands(ev.target.value))}
                              className="text-right font-mono h-9" placeholder="0" />
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 block">Dasar Komisi</Label>
                            <Input value={row.dasar_komisi || ''}
                              onChange={(ev) => toggleRow(e.id, 'dasar_komisi', formatThousands(ev.target.value))}
                              className="text-right font-mono h-9" placeholder="0" />
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 block">Rate (%)</Label>
                            <Input value={row.rate_komisi || ''}
                              onChange={(ev) => toggleRow(e.id, 'rate_komisi', ev.target.value.replace(/[^0-9.,]/g, ''))}
                              className="text-right font-mono h-9" placeholder="0" />
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase tracking-widest text-emerald-700 mb-1 block">Komisi</Label>
                            <div className="h-9 flex items-center justify-end px-3 border border-emerald-200 bg-emerald-50 rounded-md text-emerald-900 font-mono text-sm">
                              {formatRp(komisi)}
                            </div>
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase tracking-widest text-red-700 mb-1 block">Potongan (Absen)</Label>
                            <Input value={row.potongan || ''}
                              onChange={(ev) => toggleRow(e.id, 'potongan', formatThousands(ev.target.value))}
                              className="text-right font-mono h-9 border-red-200 focus-visible:ring-red-300" placeholder="0" />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {employees.length === 0 && (
                  <div className="text-center text-stone-500 py-6 text-sm">
                    Belum ada karyawan. Tambahkan di tab Karyawan terlebih dahulu.
                  </div>
                )}
              </div>
              <p className="text-[11px] text-stone-500 mt-2">
                Komisi = Dasar Komisi × Rate%. Contoh: 300.000.000 × 0,7% = 2.100.000
              </p>
            </div>

            <div className="border-t-2 border-stone-300 pt-3 flex items-center justify-between">
              <span className="text-lg font-semibold text-stone-900">Total Gaji</span>
              <span className="num text-2xl font-bold text-emerald-900" data-testid="proses-total-gaji">
                {formatRp(totalGaji)}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenRun(false)}>Batal</Button>
            <Button onClick={submitProses}
              disabled={totalGaji <= 0}
              className="bg-emerald-800 hover:bg-emerald-900 text-white disabled:opacity-50"
              data-testid="proses-gaji-submit"
            >
              <Save className="w-4 h-4 mr-1" /> Proses &amp; Buat Jurnal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Riwayat Gaji dialog */}
      <Dialog open={openExport} onOpenChange={(v) => { setOpenExport(v); if (!v) setExportRange({ from: '', to: '' }); }}>
        <DialogContent className="max-w-sm" data-testid="export-runs-dialog">
          <DialogHeader>
            <DialogTitle>Export Riwayat Gaji</DialogTitle>
            <DialogDescription>
              Pilih rentang tanggal. Hanya data pada periode ini yang akan diexport.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Tanggal Mulai</Label>
              <Input type="date" value={exportRange.from}
                onChange={(e) => setExportRange({ ...exportRange, from: e.target.value })}
                data-testid="export-runs-from" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Tanggal Akhir</Label>
              <Input type="date" value={exportRange.to}
                onChange={(e) => setExportRange({ ...exportRange, to: e.target.value })}
                data-testid="export-runs-to" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenExport(false)}>Batal</Button>
            <Button onClick={exportRunsToExcel}
              className="bg-emerald-800 hover:bg-emerald-900 text-white"
              data-testid="export-runs-confirm">
              <Download className="w-4 h-4 mr-1" /> Export Excel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AccountingLayout>
  );
}
